import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Search, Download, Zap, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useBrand } from "@/context/BrandContext";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import { Amount, WoCode } from "@/components/common/Amount";
import {
  WO_STATUSES, PRIORIDADES, BILLING_STATUSES, QUICK_VIEWS,
  woStatusTone, semaforoTone, prioridadTone, billingTone, dueRelative,
} from "@/lib/wo";
import { formatDate } from "@/lib/format";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { EmptyState } from "@/components/common/EmptyState";
import { StatusChip } from "@/components/common/StatusChip";
import { WorkOrderDialog } from "@/features/workorders/WorkOrderDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const PAGE_SIZE = 25;

const SEMAFORO_TEXT = { rojo: "text-state-critical", ambar: "text-state-warn", verde: "text-state-ok", gris: "text-ink-tertiary" };
const SEMAFORO_DOT = { rojo: "var(--state-critical)", ambar: "var(--state-warn)", verde: "var(--state-ok)", gris: "var(--ink-tertiary)" };

export default function WorkOrdersPage() {
  const { t } = useT();
  const { brandParam } = useBrand();
  const { can } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [dialog, setDialog] = useState(null);
  const [selected, setSelected] = useState([]);

  const get = (k, d = "") => params.get(k) || d;
  const patch = (changes) => {    const next = new URLSearchParams(params);
    Object.entries(changes).forEach(([k, v]) => {
      if (!v || v === "all") next.delete(k);
      else next.set(k, v);
    });
    if (!("page" in changes)) next.delete("page");
    setParams(next, { replace: true });
  };

  const vista = get("vista");
  const page = Number(get("page", "1"));
  const sort = get("sort", "created_at");
  const order = Number(get("order", "-1"));

  useEffect(() => {
    if (params.get("new") === "1") {
      setDialog("new");
      const next = new URLSearchParams(params);
      next.delete("new");
      setParams(next, { replace: true });
    }
  }, [params, setParams]);

  const filters = {
    q: get("q") || undefined,
    vista: vista || undefined,
    company_id: get("company_id") || undefined,
    property_id: get("property_id") || undefined,
    status: get("status") || undefined,
    prioridad: get("prioridad") || undefined,
    sub_id: get("sub_id") || undefined,
    responsable_user_id: get("responsable_user_id") || undefined,
    billing_status: get("billing_status") || undefined,
    desde: get("desde") || undefined,
    hasta: get("hasta") || undefined,
  };

  const { data, isLoading } = useQuery({
    queryKey: ["work-orders", { ...filters, page, sort, order, brandParam }],
    queryFn: async () =>
      (await api.get("/work-orders", {
        params: { ...filters, brand: brandParam, page, page_size: PAGE_SIZE, sort, order },
      })).data,
  });

  const { data: summary } = useQuery({
    queryKey: ["wo-summary", brandParam],
    queryFn: async () =>
      (await api.get("/work-orders/summary", { params: { brand: brandParam } })).data,
  });

  const { data: properties } = useQuery({
    queryKey: ["properties-options", brandParam],
    queryFn: async () =>
      (await api.get("/properties", { params: { page_size: 200, brand: brandParam } })).data,
  });

  const { data: subs } = useQuery({
    queryKey: ["subs-options"],
    enabled: can("subs", "read"),
    queryFn: async () => (await api.get("/subs")).data,
  });

  const { data: users } = useQuery({
    queryKey: ["users-options"],
    enabled: can("users", "read"),
    queryFn: async () => (await api.get("/users")).data,
  });

  const rows = useMemo(() => data?.items || [], [data]);
  const showCost = rows.some((r) => r.sub_cost_total_cents !== undefined);
  const showPrice = rows.some((r) => r.client_price_total_cents !== undefined);
  const showMargin = rows.some((r) => r.margen_cents !== undefined);
  const showPago = rows.some((r) => r.pago_semaforo !== undefined);

  const bulk = useMutation({
    mutationFn: async (payload) => (await api.patch("/work-orders/bulk", payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["work-orders"] });
      setSelected([]);
      toast.success(t("app.saved"));
    },
    onError: (e) => toast.error(apiError(e, t("app.error"))),
  });

  const toggle = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allSelected = rows.length > 0 && rows.every((r) => selected.includes(r.id));

  const exportCsv = async () => {
    const res = await api.get("/work-orders/export", {
      params: { brand: brandParam, vista: filters.vista, status: filters.status,
        ids: selected.length ? selected.join(",") : undefined },
      responseType: "blob",
    });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "ordenes.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const columns = useMemo(() => {
    const cols = [
      {
        key: "select",
        width: 36,
        label: (
          <Checkbox
            data-testid="wo-select-all"
            checked={allSelected}
            onCheckedChange={(v) => setSelected(v ? rows.map((r) => r.id) : [])}
          />
        ),
        render: (r) => (
          <span onClick={(e) => e.stopPropagation()}>
            <Checkbox
              data-testid={`wo-select-${r.code}`}
              checked={selected.includes(r.id)}
              onCheckedChange={() => toggle(r.id)}
            />
          </span>
        ),
      },
      {
        key: "code",
        label: t("wo.code"),
        width: 128,
        sortable: true,
        render: (r) => <WoCode code={r.code} className="text-[12.5px]" />,
      },
      {
        key: "property_nombre",
        label: t("wo.property"),
        sortable: true,
        render: (r) => (
          <p className="truncate text-[13px] text-ink">
            {r.property_nombre}
            {r.unit_etiqueta && <span className="text-ink-tertiary"> · {r.unit_etiqueta}</span>}
          </p>
        ),
      },
      {
        key: "service_nombre",
        label: t("wo.service"),
        width: 160,
        render: (r) => (
          <span className="inline-flex items-center gap-1.5 text-[12.5px] text-ink">
            <span className="h-1.5 w-1.5 shrink-0 rounded-full"
              style={{ backgroundColor: r.service_color || "var(--ink-tertiary)" }} />
            <span className="truncate">{r.service_nombre}</span>
          </span>
        ),
      },
      {
        key: "prioridad",
        label: t("wo.priority"),
        width: 92,
        render: (r) => <StatusChip tone={prioridadTone(r.prioridad)} label={t(`priority.${r.prioridad}`)} />,
      },
      {
        key: "status",
        label: t("app.status"),
        width: 128,
        sortable: true,
        render: (r) => (
          <span className="inline-flex items-center gap-1.5">
            <StatusChip tone={woStatusTone(r.status)} label={t(`woStatus.${r.status}`)}
              testId={`wo-status-${r.code}`} />
            {!!r.adicionales_pendientes && (
              <span data-testid={`wo-extra-flag-${r.code}`} title={t("wo.additionals.pending")}
                className="inline-flex h-[16px] items-center gap-1 border border-state-warn px-1 font-mono text-[10.5px] font-semibold text-state-warn">
                <Zap className="h-2.5 w-2.5" /> {r.adicionales_pendientes}
              </span>
            )}
          </span>
        ),
      },
      {
        key: "sub_nombre",
        label: t("wo.sub"),
        width: 140,
        render: (r) => r.sub_nombre ? <span className="truncate text-[12.5px] text-ink">{r.sub_nombre}</span> : <span className="text-ink-tertiary">—</span>,
      },
      {
        key: "due_at",
        label: t("wo.due"),
        width: 148,
        sortable: true,
        render: (r) => (
          <span className={cn("inline-flex items-center gap-1.5 font-mono text-[12.5px]", SEMAFORO_TEXT[r.semaforo] || "text-ink")}>
            {r.semaforo !== "gris" && (
              <span className="h-[5px] w-[5px] shrink-0 rounded-full" style={{ backgroundColor: SEMAFORO_DOT[r.semaforo] }} />
            )}
            {formatDate(r.due_at)}
            {r.semaforo !== "gris" && <span className="text-[11px] text-ink-tertiary">{dueRelative(r.due_at)}</span>}
          </span>
        ),
      },
    ];
    if (showCost)
      cols.push({
        key: "sub_cost_total_cents", label: t("wo.cost"), width: 100, align: "right",
        render: (r) => <Amount cents={r.sub_cost_total_cents} className="text-[12.5px]" />,
      });
    if (showPrice)
      cols.push({
        key: "client_price_total_cents", label: t("wo.price"), width: 100, align: "right",
        render: (r) => <Amount cents={r.client_price_total_cents} className="text-[12.5px]" />,
      });
    if (showMargin)
      cols.push({
        key: "margen_cents", label: t("wo.margin"), width: 130, align: "right",
        render: (r) => (
          <span className={cn("inline-flex items-baseline gap-1.5", r.margen_cents < 0 ? "text-state-critical" : "text-state-ok")}>
            <Amount cents={r.margen_cents} className="text-[12.5px]" />
            <span className="font-mono text-[11px] text-ink-tertiary">{r.margen_pct === null || r.margen_pct === undefined ? t("wo.margin_na") : `${r.margen_pct}%`}</span>
          </span>
        ),
      });
    cols.push({
      key: "billing_status", label: t("wo.billing"), width: 114,
      render: (r) => <StatusChip tone={billingTone(r.billing_status)} label={t(`billing.${r.billing_status}`)} />,
    });
    if (showPago)
      cols.push({
        key: "pago_semaforo", label: t("wo.pago_sub"), width: 126,
        render: (r) => r.pago_semaforo ? (
          <StatusChip testId={`wo-pago-semaforo-${r.code}`} tone={semaforoTone(r.pago_semaforo)}
            label={t(`payment_semaforo.${r.pago_semaforo}`)} />
        ) : <span className="text-ink-tertiary">—</span>,
      });
    return cols;
  }, [t, rows, selected, allSelected, showCost, showPrice, showMargin, showPago]);

  const activeFilters = ["q", "status", "prioridad", "company_id", "property_id", "sub_id",
    "billing_status", "desde", "hasta", "vista"].filter((k) => params.get(k));

  return (
    <>
      <PageHeader
        title={t("wo.title")}
        subtitle={t("wo.subtitle")}
        testId="wo-header"
        actions={
          can("work_orders", "create") && (
            <>
              <Button
                variant="outline"
                data-testid="new-turnover-button"
                onClick={() => setDialog("turnover")}
                className="h-8"
              >
                <Zap className="mr-1.5 h-3.5 w-3.5" />
                {t("wo.new_turnover")}
              </Button>
              <Button data-testid="new-wo-button" onClick={() => setDialog("new")} className="h-8">
                <Plus className="mr-1.5 h-3.5 w-3.5" />
                {t("wo.new")}
              </Button>
            </>
          )
        }
      />

      <div className="flex h-10 items-center gap-1 border-b border-line bg-paper px-6">
        {["", ...QUICK_VIEWS].map((v) => (
          <button
            key={v || "todas"}
            data-testid={`wo-view-${v || "todas"}`}
            onClick={() => patch({ vista: v })}
            className={cn(
              "inline-flex h-10 items-center gap-1.5 border-b-2 px-2.5 text-[12.5px] font-medium transition-colors duration-[120ms]",
              vista === v ? "border-brand text-ink" : "border-transparent text-ink-secondary hover:text-ink"
            )}
          >
            {t(`wo.views.${v || "todas"}`)}
            {v && summary?.grupos?.[v] != null && (
              <span className="font-mono text-[11px] text-ink-tertiary">{summary.grupos[v]}</span>
            )}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 divide-x divide-line border-b border-line bg-paper px-6 py-2.5">
        <div className="relative pr-3">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-tertiary" />
          <Input
            data-testid="wo-search-input"
            key={get("q") || "empty"}
            defaultValue={get("q")}
            onKeyDown={(e) => e.key === "Enter" && patch({ q: e.currentTarget.value })}
            onBlur={(e) => patch({ q: e.currentTarget.value })}
            placeholder={t("wo.search")}
            className="h-8 w-[260px] pl-8"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2 pl-0 pr-3">
          <Select value={get("status", "all")} onValueChange={(v) => patch({ status: v })}>
            <SelectTrigger data-testid="wo-status-filter" className="h-8 w-[144px]">
              <SelectValue placeholder={t("app.status")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("app.status")}: {t("app.all")}</SelectItem>
              {WO_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>{t(`woStatus.${s}`)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={get("prioridad", "all")} onValueChange={(v) => patch({ prioridad: v })}>
            <SelectTrigger data-testid="wo-priority-filter" className="h-8 w-[124px]">
              <SelectValue placeholder={t("wo.priority")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("wo.priority")}: {t("app.all")}</SelectItem>
              {PRIORIDADES.map((p) => (
                <SelectItem key={p} value={p}>{t(`priority.${p}`)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={get("property_id", "all")} onValueChange={(v) => patch({ property_id: v })}>
            <SelectTrigger data-testid="wo-property-filter" className="h-8 w-[180px]">
              <SelectValue placeholder={t("wo.property")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("wo.property")}: {t("app.all")}</SelectItem>
              {(properties?.items || []).map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {can("subs", "read") && (
            <Select value={get("sub_id", "all")} onValueChange={(v) => patch({ sub_id: v })}>
              <SelectTrigger data-testid="wo-sub-filter" className="h-8 w-[160px]">
                <SelectValue placeholder={t("wo.sub")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("wo.sub")}: {t("app.all")}</SelectItem>
                {(subs?.items || []).map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Select value={get("billing_status", "all")} onValueChange={(v) => patch({ billing_status: v })}>
            <SelectTrigger data-testid="wo-billing-filter" className="h-8 w-[140px]">
              <SelectValue placeholder={t("wo.billing")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("wo.billing")}: {t("app.all")}</SelectItem>
              {BILLING_STATUSES.map((b) => (
                <SelectItem key={b} value={b}>{t(`billing.${b}`)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2 pl-0">
          <Input
            type="date"
            data-testid="wo-from-filter"
            value={get("desde")}
            onChange={(e) => patch({ desde: e.target.value })}
            className="h-8 w-[130px] font-mono text-[12px]"
          />
          <Input
            type="date"
            data-testid="wo-to-filter"
            value={get("hasta")}
            onChange={(e) => patch({ hasta: e.target.value })}
            className="h-8 w-[130px] font-mono text-[12px]"
          />
        </div>
        {!!activeFilters.length && (
          <Button
            variant="ghost"
            data-testid="wo-clear-filters"
            onClick={() => setParams(new URLSearchParams(), { replace: true })}
            className="h-8 text-ink-secondary hover:text-ink"
          >
            <X className="mr-1 h-3.5 w-3.5" /> {t("wo.clear_filters")}
          </Button>
        )}
        {can("work_orders", "export") && (
          <Button variant="outline" data-testid="wo-export" onClick={exportCsv} className="ml-auto h-8">
            <Download className="mr-1.5 h-3.5 w-3.5" /> {t("wo.export")}
          </Button>
        )}
      </div>

      {!!selected.length && (
        <div
          data-testid="wo-bulk-bar"
          className="flex items-center gap-3 border-b border-line bg-ink px-6 py-2 text-[12.5px] text-paper"
        >
          <span className="font-mono font-medium">{selected.length} {t("wo.selected")}</span>
          <Select onValueChange={(v) => bulk.mutate({ ids: selected, prioridad: v })}>
            <SelectTrigger data-testid="wo-bulk-priority" className="h-7 w-[160px] border-line-strong bg-ink text-[12.5px] text-paper">
              <SelectValue placeholder={t("wo.bulk_priority")} />
            </SelectTrigger>
            <SelectContent>
              {PRIORIDADES.map((p) => (
                <SelectItem key={p} value={p}>{t(`priority.${p}`)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {can("users", "read") && (
            <Select onValueChange={(v) => bulk.mutate({ ids: selected, responsable_user_id: v })}>
              <SelectTrigger data-testid="wo-bulk-responsable" className="h-7 w-[180px] border-line-strong bg-ink text-[12.5px] text-paper">
                <SelectValue placeholder={t("wo.bulk_responsable")} />
              </SelectTrigger>
              <SelectContent>
                {(users?.items || []).map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button variant="ghost" data-testid="wo-bulk-export" onClick={exportCsv}
            className="h-7 px-2 text-[12.5px] text-paper hover:opacity-80">
            <Download className="mr-1.5 h-3.5 w-3.5" /> {t("wo.export")}
          </Button>
          <button onClick={() => setSelected([])} className="ml-auto text-ink-tertiary hover:text-paper">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="flex min-h-0 flex-1 flex-col bg-paper" data-testid="wo-page">
        <DataTable
          testId="wo-table"
          columns={columns}
          rows={rows}
          total={data?.total || 0}
          page={page}
          pageSize={PAGE_SIZE}
          onPageChange={(p) => patch({ page: String(p) })}
          loading={isLoading}
          sort={sort}
          order={order}
          onSortChange={(key) =>
            patch({ sort: key, order: String(sort === key ? -order : -1) })
          }
          onRowClick={(r) => navigate(`/ordenes/${r.id}`)}
          isRowSelected={(r) => selected.includes(r.id)}
          empty={
            <EmptyState
              testId="wo-empty"
              title={t("wo.empty_title")}
              description={t("wo.empty_desc")}
              action={
                can("work_orders", "create") && (
                  <Button data-testid="wo-empty-new" onClick={() => setDialog("new")} className="h-8">
                    {t("wo.new")}
                  </Button>
                )
              }
            />
          }
        />
      </div>

      <WorkOrderDialog
        open={!!dialog}
        turnover={dialog === "turnover"}
        onOpenChange={(v) => !v && setDialog(null)}
      />
    </>
  );
}
